// Copyright ©️ 2026 eVolve MEP, LLC

import { useState, useMemo } from 'react';

import type { HubData, ItemData, ProjectData } from '@aps_sdk/data-management';
import { type ProjectId, type AutodeskCloudModel, useEvolvePost, useEvolveGet, formatDate } from '@evolvemep/api-ts';
import { ActionIcon, Button, Flex, Loader, Popover, Text, useMantineTheme } from '@mantine/core';
import { parseISO } from 'date-fns';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { VariableTrackingModal } from 'components/Mantine/Modals/VariableTrackingModal';
import { isNotNil } from 'helpers/isNotNil';
import { UnlinkModelModal } from 'modules/Shop/ModelBrowser/UnlinkModelModal';

const BlockedLinkText = ({ projectId }: { projectId: ProjectId }) => {
  const { data, loading } = useEvolveGet(`shop/project/${projectId}`, { lazy: false });

  if (loading) return <Loader />;

  return (
    <Text>
      This model is already linked to {data ? <b> {data.projectName}</b> : <> another project</>}. Would you like to
      unlink it from that project and link it to the selected project instead?
    </Text>
  );
};

type Props = {
  model: ItemData;
  linked: boolean;
  projectId: ProjectId;
  selectedHub: NonNullable<HubData['id']>;
  selectedAutodeskProject: NonNullable<ProjectData['id']>;
  onModelLinked: (model: AutodeskCloudModel['urn']) => void;
  onModelUnlinked: (model: AutodeskCloudModel['urn']) => void;
};

const validModelExtensions = ['.rvt', '.f3d'];

export const ModelLinkAndPopover = ({
  model,
  linked,
  projectId,
  selectedAutodeskProject,
  selectedHub,
  onModelLinked,
  onModelUnlinked,
}: Props) => {
  const theme = useMantineTheme();
  const [blockingProjectId, setBlockingProjectId] = useState<ProjectId | null>(null);
  const [unlinking, setUnlinking] = useState(false);
  const [opened, setOpened] = useState(false);
  const urn = model.id as AutodeskCloudModel['urn'];

  const { apiCall: checkForOtherProjectLink } = useEvolveGet(`shop/autodesk/autodeskModel/:urn`);

  const { apiCall: link, loading: linking } = useEvolvePost(`shop/autodesk/linkModelToProject`);

  const onLinkOrUnlink = async () => {
    if (linked) {
      setUnlinking(true);
    } else {
      // Check if there is already a model linked to another project.
      // Ignore errors, since the most likely cause of an error is a 404,
      // which would indicate that we didn't find a link to this model.
      const res = await checkForOtherProjectLink({
        url: `shop/autodesk/autodeskModel/${urn}`,
      }).catch(() => null);
      if (isNotNil(res?.projectId)) {
        setBlockingProjectId(res.projectId);
        setOpened(false);
        return;
      }
      await link({
        urn: model.id as AutodeskCloudModel['urn'],
        projectId,
        autodeskHubId: selectedHub,
        autodeskProjectId: selectedAutodeskProject,
      });
      onModelLinked(urn);
    }
    setOpened(false);
  };

  const isValidModel = useMemo(
    () => validModelExtensions.some((extension) => model.attributes.displayName.toLowerCase().endsWith(extension)),
    [model.attributes.displayName],
  );

  return (
    <>
      <Popover position="bottom-end" shadow="md" opened={opened} onChange={setOpened}>
        <Popover.Target>
          <Flex
            className={isValidModel ? 'link' : undefined}
            c="black"
            gap="xs"
            style={{ overflow: 'hidden' }}
            align="center"
            onClick={() => setOpened((o) => isValidModel && !o)}
          >
            <Flex w={24} miw={24} justify="center">
              {linked ? <EvolveIcon icon="Link" color={theme.primaryColor} /> : null}
            </Flex>
            <Text
              title={model.attributes.displayName}
              style={{ textWrap: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}
              c={isValidModel ? undefined : 'dimmed'}
            >
              {model.attributes.displayName}
            </Text>
          </Flex>
        </Popover.Target>
        <Popover.Dropdown miw={250} maw={600}>
          <ActionIcon onClick={() => setOpened(false)} style={{ position: 'absolute', right: 5, top: 5 }}>
            <EvolveIcon icon="Close" size="sm" color="dark" />
          </ActionIcon>
          <Text mb={3} mr="lg" style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>
            {model.attributes.displayName}
          </Text>
          <Text fz="sm">{model.attributes.lastModifiedUserName}</Text>
          <Text fz="sm">{formatDate(parseISO(model.attributes.lastModifiedTime), 'P p')}</Text>
          <Button
            mt="xs"
            onClick={onLinkOrUnlink}
            loading={linking}
            leftSection={<EvolveIcon icon={linked ? 'Unlink' : 'Link'} />}
          >
            {linked ? 'Unlink from project' : 'Link to project'}
          </Button>
        </Popover.Dropdown>
      </Popover>

      <VariableTrackingModal
        title="Link model"
        confirmationText="Link model"
        variable={blockingProjectId}
        onConfirm={async () => {
          await link({
            urn,
            projectId,
            autodeskHubId: selectedHub,
            autodeskProjectId: selectedAutodeskProject,
          });
          onModelLinked(urn);
          setBlockingProjectId(null);
        }}
        onClose={() => setBlockingProjectId(null)}
      >
        {(p) => <BlockedLinkText projectId={p} />}
      </VariableTrackingModal>

      <UnlinkModelModal
        refresh={() => onModelUnlinked(urn)}
        modelToUnlink={
          unlinking
            ? {
                urn,
                projectId,
              }
            : null
        }
        onClose={() => {
          setUnlinking(false);
          setOpened(false);
        }}
      />
    </>
  );
};
